import React from 'react';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import './ClientInfo.scss';
import UpdateClientForm, { UpdateClientFormValues } from '../forms/UpdateClientForm';
import { updateClient } from '$/store/client/actions';
import { ClientContact } from '$/core/models';
import Loading from '$/components/loading/Loading';
import { getClientProvidedDataSitesEnabled, getCloPermissioningEnabled } from '$/store/release-feature-flags/selectors';
import { toastError } from '$/store/toast/actions'; // <-- Import toastError

interface IClientInfoProps {
  client?: Bixby.Models.Client;
  subscriptions: Bixby.Models.IIdentifiable[];
  isLoading: boolean;
  isCloPermissioningEnabled: boolean;
  isClientProvidedDataSitesEnabled: boolean;
  dispatchUpdateClient: (id: string, client: Bixby.Models.UpdateClientRequest) => Promise<void>;
  dispatchToastError: (msg: string) => void; // <-- Add toast dispatcher to props
}

const ClientInfo: React.FC<IClientInfoProps> = props => {
  const {
    client,
    dispatchUpdateClient,
    isLoading,
    isCloPermissioningEnabled,
    isClientProvidedDataSitesEnabled,
    dispatchToastError,
  } = props;

  if (!client) {
    return null;
  }
  const currentClient = { ...client };

  const handleSaveAsDraft = async (values: UpdateClientFormValues) => {
    const primaryContact1 = currentClient.contacts.find(user => user.email === `${values.primaryContact1}`);
    if (!primaryContact1 && !values.usersCannotLogin && !values.isCutoverToCreditSights) {
      dispatchToastError('A valid Primary Admin is required.');
      return;
    }

    if (values.hasBixbyCompliance) {
      const primaryComplianceAdmin = currentClient.contacts.find(
        user => user.email === `${values.primaryComplianceAdmin}`
      );
      if (!primaryComplianceAdmin) {
        dispatchToastError('A valid Primary Compliance Admin is required.');
        return;
      }

      if (Bixby.Utils.isNullOrEmpty(values.mnpiDataSiteEmail)) {
        dispatchToastError('MNPI Data Site Email is required.');
        return;
      }
    }

    let primaryComplianceAdmin_Id: string | null = null;
    let mnpiDataSiteEmail: string | null = null;
    let hasBixbyComplianceAutoApproval: boolean | null = null;
    let hasBixbyComplianceDemoMode: boolean | null = null;
    let isPrivateOnAllNames: boolean | null = null;
    let canReachOutToBankOnBehalf: boolean | null = null;

    if (values.hasBixbyCompliance) {
      const primaryComplianceAdminEmail = !Bixby.Utils.isNullOrEmpty(values.primaryComplianceAdmin)
        ? `${values.primaryComplianceAdmin}`
        : null;
      primaryComplianceAdmin_Id =
        currentClient.contacts.find(user => user.email === primaryComplianceAdminEmail)?.id || null;

      mnpiDataSiteEmail = !Bixby.Utils.isNullOrEmpty(values.mnpiDataSiteEmail)
        ? `${values.mnpiDataSiteEmail}@${currentClient.emailDomain.split(',')[0]}`
        : null;

      hasBixbyComplianceAutoApproval = values.hasBixbyComplianceAutoApproval;
      hasBixbyComplianceDemoMode = values.hasBixbyComplianceDemoMode;
      isPrivateOnAllNames = values.isPrivateOnAllNames;
      canReachOutToBankOnBehalf = values.canReachOutToBankOnBehalf;
    }

    const request: Bixby.Models.UpdateClientRequest = {
      id: currentClient.id,
      bixbyClientEmail: `${values.clientEmail}@${Bixby.Constants.EMAIL_DOMAIN_BIXBY}`,
      dataSiteContact1: !Bixby.Utils.isNullOrEmpty(values.dataSiteContact1)
        ? `${values.dataSiteContact1}@${currentClient.emailDomain.split(',')[0]}`
        : null,
      dataSiteContact2: !Bixby.Utils.isNullOrEmpty(values.dataSiteContact2)
        ? `${values.dataSiteContact2}@${currentClient.emailDomain.split(',')[0]}`
        : null,
      emailDomain: values.emailDomain,
      name: currentClient.name,
      notes: values.clientNotes,
      subscription_Id: values.subscriptionTier,
      primaryContacts: primaryContact1 ? [{ id: primaryContact1.id, sort: 0 }] : [],
      hasAnalytics: values.hasAnalytics,
      hasExcelAccess: values.hasExcelAccess,
      hasCompanyTranscriptions: values.hasCompanyTranscriptions,
      hasCompanyDealNotes: values.hasCompanyDealNotes,
      hasCompanyDocuments: values.hasCompanyDocuments,
      hasCloAccess: values.hasCloAccess,
      hasCreditSightsCovenantReview: values.hasCreditSightsCovenantReview,
      hasPreapprovedCompanyAccess: values.hasPreapprovedCompanyAccess,
      primaryComplianceAdmin_Id,
      mnpiDataSiteEmail,
      hasBixbyCompliance: values.hasBixbyCompliance,
      hasBixbyComplianceAutoApproval,
      hasBixbyComplianceDemoMode,
      isPrivateOnAllNames,
      canReachOutToBankOnBehalf,
      usersCannotLogin: values.usersCannotLogin,
      hasSFDR: values.hasSFDR,
      hasUsEuroGrouping: values.hasUsEuroGrouping,
      isSufPermissioned: values.isSufPermissioned,
      isRatingsPermissioned: values.isRatingsPermissioned,
      isOnFitchPaper: values.isOnFitchPaper,
      isCovenantCrossPermissioned: values.isCovenantCrossPermissioned,
      isInPreview: values.isInPreview,
      isCutoverToCreditSights: values.isCutoverToCreditSights,
      allowDocumentSharing: values.allowDocumentSharing,
      hasClientProvidedDataSites: values.hasClientProvidedDataSites,
      hasCloPermissions: values.hasCloPermissions,
      includeWithDataAggregation: values.includeWithDataAggregation,
      hasPortfolioManagerWorkflow: values.hasPortfolioManagerWorkflow,
    };

    if (request.primaryContacts && !Bixby.Utils.isNullOrEmpty(values.primaryContact2)) {
      const primaryContact2 = currentClient.contacts.find(user => user.email === `${values.primaryContact2}`);
      if (primaryContact2) {
        request.primaryContacts.push({ id: primaryContact2.id, sort: 1 });
      }
    }

    await dispatchUpdateClient(currentClient.id, request);
    return Promise.resolve();
  };

  const primaryContacts: ClientContact[] = Bixby.Utils.orderBy(
    Bixby.Utils.filter(currentClient.contacts || [], u => u.primarySort != null),
    u => u.primarySort
  );

  const initialValues: UpdateClientFormValues = {
    clientName: currentClient.name,
    clientEmail: Bixby.Utils.first(currentClient.bixbyClientEmail.split('@')),
    dataSiteContact1: currentClient.dataSiteContact1
      ? Bixby.Utils.first(currentClient.dataSiteContact1.split('@'))
      : '',
    dataSiteContact2: currentClient.dataSiteContact2
      ? Bixby.Utils.first(currentClient.dataSiteContact2.split('@'))
      : '',
    dataSiteEmail: Bixby.Utils.first(currentClient.dataSiteEmail.split('@')),
    mnpiDataSiteEmail: currentClient.mnpiDataSiteEmail
      ? Bixby.Utils.first(currentClient.mnpiDataSiteEmail.split('@'))
      : '',
    emailDomain: currentClient.emailDomain,
    clientNotes: currentClient.notes,
    subscriptionTier: currentClient.subscription_Id,
    primaryContact1: primaryContacts.length > 0 && primaryContacts[0].primarySort === 0 ? primaryContacts[0].email : '',
    primaryContact2: primaryContacts.length > 1 && primaryContacts[1].primarySort === 1 ? primaryContacts[1].email : '',
    hasAnalytics: currentClient.hasAnalytics,
    hasExcelAccess: currentClient.hasExcelAccess,
    hasCompanyTranscriptions: currentClient.hasCompanyTranscriptions,
    hasCompanyDealNotes: currentClient.hasCompanyDealNotes,
    hasCompanyDocuments: currentClient.hasCompanyDocuments,
    hasCloAccess: currentClient.hasCloAccess,
    hasClientProvidedDataSites: currentClient.hasClientProvidedDataSites,
    hasCloPermissions: currentClient.hasCloPermissions,
    hasCreditSightsCovenantReview: currentClient.hasCreditSightsCovenantReview,
    hasPreapprovedCompanyAccess: currentClient.hasPreapprovedCompanyAccess,
    primaryComplianceAdmin: currentClient.primaryComplianceAdmin_Id
      ? currentClient.contacts.find(_ => _.id === currentClient.primaryComplianceAdmin_Id)?.email
      : '',
    hasBixbyCompliance: currentClient.hasBixbyCompliance,
    hasBixbyComplianceAutoApproval:
      currentClient.hasBixbyComplianceAutoApproval == null ? false : currentClient.hasBixbyComplianceAutoApproval,
    hasBixbyComplianceDemoMode:
      currentClient.hasBixbyComplianceDemoMode == null ? false : currentClient.hasBixbyComplianceDemoMode,
    isPrivateOnAllNames: currentClient.isPrivateOnAllNames == null ? false : currentClient.isPrivateOnAllNames,
    canReachOutToBankOnBehalf:
      currentClient.canReachOutToBankOnBehalf == null ? false : currentClient.canReachOutToBankOnBehalf,
    usersCannotLogin: currentClient.usersCannotLogin,
    hasSFDR: currentClient.hasSFDR,
    hasUsEuroGrouping: currentClient.hasUsEuroGrouping,
    isSufPermissioned: currentClient.isSufPermissioned,
    isRatingsPermissioned: currentClient.isRatingsPermissioned,
    isOnFitchPaper: currentClient.isOnFitchPaper,
    isCovenantCrossPermissioned: currentClient.isCovenantCrossPermissioned,
    isInPreview: currentClient.isInPreview,
    isCutoverToCreditSights: currentClient.isCutoverToCreditSights,
    allowDocumentSharing: currentClient.allowDocumentSharing,
    includeWithDataAggregation: currentClient.includeWithDataAggregation,
    hasPortfolioManagerWorkflow: currentClient.hasPortfolioManagerWorkflow,
  };

  return isLoading ? (
    <Loading pageLevel />
  ) : (
    <section className="client_info">
      <UpdateClientForm
        initialValues={initialValues}
        isCloPermissioningEnabled={isCloPermissioningEnabled}
        isClientProviderDataSitesEnabled={isClientProvidedDataSitesEnabled}
        submitButtonText="Save"
        onSubmit={handleSaveAsDraft}
      />
    </section>
  );
};

const mapStateToProps = (state: AppState) => {
  const { isCloPermissioningEnabled } = getCloPermissioningEnabled(state);
  const { isClientProvidedDataSitesEnabled } = getClientProvidedDataSitesEnabled(state);

  return {
    isLoading: state.clientState.isLoading,
    client: state.clientState.currentClient,
    subscriptions: state.subscriptionState.data || [],
    isCloPermissioningEnabled,
    isClientProvidedDataSitesEnabled,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateClient: (id: string, client: Bixby.Models.UpdateClientRequest) => dispatch(updateClient(id, client)),
  dispatchToastError: (msg: string) => dispatch(toastError(msg)), // <-- Add toast dispatcher
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientInfo);
